import { html, nothing } from "lit";
import type { SkillMessageMap } from "../controllers/skills.ts";
import type { SkillStatusEntry, SkillStatusReport } from "../types.ts";
import { clampText } from "../format.ts";

type SkillGroup = {
  id: string;
  label: string;
  skills: SkillStatusEntry[];
};

const SKILL_SOURCE_GROUPS: Array<{ id: string; label: string; sources: string[] }> = [
  { id: "workspace", label: "Workspace Skills", sources: ["openclaw-workspace"] },
  { id: "built-in", label: "Built-in Skills", sources: ["openclaw-bundled"] },
  { id: "installed", label: "Installed Skills", sources: ["openclaw-managed"] },
  { id: "extra", label: "Extra Skills", sources: ["openclaw-extra"] },
];

function groupSkills(skills: SkillStatusEntry[]): SkillGroup[] {
  const groups = new Map<string, SkillGroup>();
  for (const def of SKILL_SOURCE_GROUPS) {
    groups.set(def.id, { id: def.id, label: def.label, skills: [] });
  }
  const builtInGroup = SKILL_SOURCE_GROUPS.find((group) => group.id === "built-in");
  const other: SkillGroup = { id: "other", label: "Other Skills", skills: [] };
  for (const skill of skills) {
    const match = skill.bundled
      ? builtInGroup
      : SKILL_SOURCE_GROUPS.find((group) => group.sources.includes(skill.source));
    if (match) {
      groups.get(match.id)?.skills.push(skill);
    } else {
      other.skills.push(skill);
    }
  }
  const ordered = SKILL_SOURCE_GROUPS.map((group) => groups.get(group.id)).filter(
    (group): group is SkillGroup => Boolean(group && group.skills.length > 0),
  );
  if (other.skills.length > 0) {
    ordered.push(other);
  }
  return ordered;
}

export type SkillsProps = {
  loading: boolean;
  report: SkillStatusReport | null;
  error: string | null;
  filter: string;
  edits: Record<string, string>;
  busyKey: string | null;
  messages: SkillMessageMap;
  /** Set of skill keys whose detail panel is expanded. */
  expandedKeys?: Set<string>;
  onFilterChange: (next: string) => void;
  onRefresh: () => void;
  onToggle: (skillKey: string, enabled: boolean) => void;
  onEdit: (skillKey: string, value: string) => void;
  onSaveKey: (skillKey: string) => void;
  onInstall: (skillKey: string, name: string, installId: string) => void;
  onToggleExpanded?: (skillKey: string) => void;
};

export function isSkillEnabled(skill: SkillStatusEntry): boolean {
  // A skill is enabled when it is neither disabled nor blocked by allowlist.
  return !skill.disabled;
}

function renderEnabledBadge(skill: SkillStatusEntry) {
  const enabled = isSkillEnabled(skill);
  const className = enabled ? "chip chip-ok" : "chip chip-warn";
  const label = enabled ? "enabled" : "disabled";
  return html`<span class=${className} data-skill-state=${enabled ? "enabled" : "disabled"}>${label}</span>`;
}

export function renderSkills(props: SkillsProps) {
  const skills = props.report?.skills ?? [];
  const filter = props.filter.trim().toLowerCase();
  const filtered = filter
    ? skills.filter((skill) =>
        [skill.name, skill.description, skill.source].join(" ").toLowerCase().includes(filter),
      )
    : skills;
  const groups = groupSkills(filtered);
  const enabledCount = filtered.filter(isSkillEnabled).length;

  return html`
    <section class="card">
      <div class="row" style="justify-content: space-between;">
        <div>
          <div class="card-title">Skills</div>
          <div class="card-sub">
            Bundled, managed, and workspace skills ·
            <strong>${enabledCount}</strong> of <strong>${filtered.length}</strong> enabled
          </div>
        </div>
        <button class="btn" ?disabled=${props.loading} @click=${props.onRefresh}>
          ${props.loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      <div class="filters" style="margin-top: 14px;">
        <label class="field" style="flex: 1;">
          <span>Filter</span>
          <input
            .value=${props.filter}
            @input=${(e: Event) => props.onFilterChange((e.target as HTMLInputElement).value)}
            placeholder="Search skills"
          />
        </label>
        <div class="muted">${filtered.length} shown</div>
      </div>

      ${
        props.error
          ? html`<div class="callout danger" style="margin-top: 12px;">${props.error}</div>`
          : nothing
      }

      ${
        filtered.length === 0
          ? html`
              <div class="muted" style="margin-top: 16px">No skills found.</div>
            `
          : html`
            <div class="agent-skills-groups" style="margin-top: 16px;">
              ${groups.map((group) => {
                const collapsedByDefault = group.id === "workspace" || group.id === "built-in";
                return html`
                  <details class="agent-skills-group" ?open=${!collapsedByDefault}>
                    <summary class="agent-skills-header">
                      <span>${group.label}</span>
                      <span class="muted">${group.skills.length}</span>
                    </summary>
                    <div class="list skills-grid">
                      ${group.skills.map((skill) => renderSkill(skill, props))}
                    </div>
                  </details>
                `;
              })}
            </div>
          `
      }
    </section>
  `;
}

function renderSkill(skill: SkillStatusEntry, props: SkillsProps) {
  const busy = props.busyKey === skill.skillKey;
  const apiKey = props.edits[skill.skillKey] ?? "";
  const message = props.messages[skill.skillKey] ?? null;
  const canInstall = skill.install.length > 0 && skill.missing.bins.length > 0;
  const showBundledBadge = Boolean(skill.bundled && skill.source !== "openclaw-bundled");
  const missing = [
    ...skill.missing.bins.map((b) => `bin:${b}`),
    ...skill.missing.env.map((e) => `env:${e}`),
    ...skill.missing.config.map((c) => `config:${c}`),
    ...skill.missing.os.map((o) => `os:${o}`),
  ];
  const reasons: string[] = [];
  if (skill.disabled) {
    reasons.push("disabled");
  }
  if (skill.blockedByAllowlist) {
    reasons.push("blocked by allowlist");
  }
  const isExpanded = props.expandedKeys?.has(skill.skillKey) ?? false;
  return html`
    <div class="list-item" data-skill-key=${skill.skillKey}>
      <div
        class="list-main"
        style="cursor: pointer;"
        @click=${() => props.onToggleExpanded?.(skill.skillKey)}
        role="button"
        tabindex="0"
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            props.onToggleExpanded?.(skill.skillKey);
          }
        }}
      >
        <div class="list-title">
          ${skill.emoji ? `${skill.emoji} ` : ""}${skill.name}
        </div>
        <div class="list-sub">${clampText(skill.description, 140)}</div>
        <div class="chip-row" style="margin-top: 6px;">
          <span class="chip">${skill.source}</span>
          ${
            showBundledBadge
              ? html`
                  <span class="chip">bundled</span>
                `
              : nothing
          }
          <span class="chip ${skill.eligible ? "chip-ok" : "chip-warn"}">
            ${skill.eligible ? "eligible" : "blocked"}
          </span>
          ${renderEnabledBadge(skill)}
        </div>
        ${
          missing.length > 0
            ? html`
              <div class="muted" style="margin-top: 6px;">
                Missing: ${missing.join(", ")}
              </div>
            `
            : nothing
        }
        ${
          reasons.length > 0
            ? html`
              <div class="muted" style="margin-top: 6px;">
                Reason: ${reasons.join(", ")}
              </div>
            `
            : nothing
        }
      </div>
      <div class="list-meta">
        <div class="row" style="justify-content: flex-end; flex-wrap: wrap; gap: 8px;">
          <label
            class="skill-toggle"
            data-action="skill-toggle"
            style="display: inline-flex; align-items: center; gap: 6px; cursor: ${busy ? "wait" : "pointer"}; user-select: none;"
          >
            <input
              type="checkbox"
              data-action="skill-toggle-input"
              data-skill-key=${skill.skillKey}
              ?disabled=${busy}
              .checked=${isSkillEnabled(skill)}
              @click=${(event: Event) => event.stopPropagation()}
              @change=${(event: Event) => {
                const input = event.target as HTMLInputElement;
                props.onToggle(skill.skillKey, input.checked);
              }}
            />
            <span style="font-size: 12px; font-weight: 600;">
              ${isSkillEnabled(skill) ? "On" : "Off"}
            </span>
          </label>
          <button
            class="btn"
            data-action="skill-toggle-button"
            ?disabled=${busy}
            @click=${(event: Event) => {
              event.stopPropagation();
              props.onToggle(skill.skillKey, !isSkillEnabled(skill));
            }}
          >
            ${busy ? "Saving…" : isSkillEnabled(skill) ? "Disable" : "Enable"}
          </button>
          ${
            canInstall
              ? html`<button
                class="btn"
                ?disabled=${busy}
                @click=${(event: Event) => {
                  event.stopPropagation();
                  props.onInstall(skill.skillKey, skill.name, skill.install[0].id);
                }}
              >
                ${busy ? "Installing…" : skill.install[0].label}
              </button>`
              : nothing
          }
        </div>
        ${
          message
            ? html`<div
              class="muted"
              data-skill-message=${skill.skillKey}
              style="margin-top: 8px; color: ${
                message.kind === "error"
                  ? "var(--danger-color, #d14343)"
                  : "var(--success-color, #0a7f5a)"
              };"
            >
              ${message.message}
            </div>`
            : nothing
        }
        ${
          isExpanded
            ? renderSkillDetail(skill, apiKey, props, busy)
            : html`
                <button
                  class="btn btn--sm"
                  style="margin-top: 10px;"
                  data-action="skill-expand"
                  @click=${(event: Event) => {
                    event.stopPropagation();
                    props.onToggleExpanded?.(skill.skillKey);
                  }}
                >
                  Show details
                </button>
              `
        }
      </div>
    </div>
  `;
}

function renderSkillDetail(
  skill: SkillStatusEntry,
  apiKey: string,
  props: SkillsProps,
  busy: boolean,
) {
  const requirements = skill.requirements ?? { bins: [], env: [], config: [], os: [] };
  const reqEntries: string[] = [];
  for (const b of requirements.bins) {
    reqEntries.push(`bin:${b}`);
  }
  for (const e of requirements.env) {
    reqEntries.push(`env:${e}`);
  }
  for (const c of requirements.config) {
    reqEntries.push(`config:${c}`);
  }
  for (const o of requirements.os) {
    reqEntries.push(`os:${o}`);
  }
  return html`
    <div class="skill-detail" data-skill-detail=${skill.skillKey} style="margin-top: 12px;">
      <div class="row" style="justify-content: space-between; align-items: center;">
        <strong>Skill details</strong>
        <button
          class="btn btn--sm"
          data-action="skill-collapse"
          @click=${(event: Event) => {
            event.stopPropagation();
            props.onToggleExpanded?.(skill.skillKey);
          }}
        >
          Hide details
        </button>
      </div>
      ${
        skill.description
          ? html`<p class="muted" style="margin-top: 8px;">${skill.description}</p>`
          : nothing
      }
      <div style="margin-top: 10px;">
        <span class="muted" style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em;">
          Configuration
        </span>
        <table class="skill-detail-table" style="width: 100%; margin-top: 6px;">
          <tbody>
            <tr>
              <th>Skill key</th>
              <td><code>${skill.skillKey}</code></td>
            </tr>
            <tr>
              <th>Source</th>
              <td>${skill.source}</td>
            </tr>
            <tr>
              <th>Always</th>
              <td>${skill.always ? "yes" : "no"}</td>
            </tr>
            <tr>
              <th>Eligible</th>
              <td>${skill.eligible ? "yes" : "no"}</td>
            </tr>
            ${
              skill.primaryEnv
                ? html`
                    <tr>
                      <th>Primary env</th>
                      <td><code>${skill.primaryEnv}</code></td>
                    </tr>
                  `
                : nothing
            }
            ${
              reqEntries.length > 0
                ? html`
                    <tr>
                      <th>Requires</th>
                      <td>${reqEntries.join(", ")}</td>
                    </tr>
                  `
                : nothing
            }
            ${
              skill.homepage
                ? html`
                    <tr>
                      <th>Homepage</th>
                      <td>${skill.homepage}</td>
                    </tr>
                  `
                : nothing
            }
          </tbody>
        </table>
      </div>
      ${
        skill.primaryEnv
          ? html`
            <div class="field" style="margin-top: 12px;">
              <span>API key</span>
              <input
                type="password"
                data-action="skill-apikey-input"
                .value=${apiKey}
                @input=${(e: Event) =>
                  props.onEdit(skill.skillKey, (e.target as HTMLInputElement).value)}
              />
            </div>
            <button
              class="btn primary"
              style="margin-top: 8px;"
              data-action="skill-apikey-save"
              ?disabled=${busy}
              @click=${(event: Event) => {
                event.stopPropagation();
                props.onSaveKey(skill.skillKey);
              }}
            >
              Save key
            </button>
          `
          : nothing
      }
    </div>
  `;
}
